import { useEffect, useRef, useCallback } from 'react';

import { driver } from 'driver.js';

import 'driver.js/dist/driver.css';

import { usePreferencesStore } from '#lib/stores/preferencesStore';

/**
 * Auto-starts the lead overview tour for first-time users and exposes a manual trigger.
 *
 * @param {Object} [options]
 * @param {boolean} [options.paused=false] - Suppresses the auto-start while true (e.g. while the
 *   search criteria modal is open); the tour starts once it turns false again.
 * @returns {{ startTour: () => void }}
 */
export const useLeadOverviewTour = ({ paused = false } = {}) => {
	const tourCompleted = usePreferencesStore((s) => s.leadOverviewTourCompleted);
	const setTourCompleted = usePreferencesStore((s) => s.setLeadOverviewTourCompleted);
	const driverRef = useRef(null);

	const createDriver = useCallback(() => {
		return driver({
			showProgress: true,
			animate: true,
			overlayColor: 'rgba(0, 0, 0, 0.6)',
			stagePadding: 8,
			stageRadius: 8,
			popoverClass: 'lead-tour-popover',
			nextBtnText: 'Weiter',
			prevBtnText: 'Zurück',
			doneBtnText: 'Verstanden',
			progressText: '{{current}} von {{total}}',
			allowKeyboardControl: false,
			onDestroyStarted: () => {
				setTourCompleted(true);
				driverRef.current?.destroy();
			},
			steps: [
				{
					popover: {
						title: 'Willkommen zur Lead-Übersicht! 👋',
						description:
							'Hier finden Sie alle Ihre gefundenen Unternehmen auf einen Blick. Wir zeigen Ihnen kurz die wichtigsten Funktionen dieser Seite.',
						side: 'over',
						align: 'center',
					},
				},
				{
					element: '[data-tour="new-search"]',
					popover: {
						title: 'Neue Suche starten',
						description:
							'Über "Neue Suche" öffnen Sie die Suchkriterien: Branche, Standort und weitere Filter – mit Live-Treffer-Vorschau. Von hier starten Sie jede neue Lead-Suche.',
						side: 'bottom',
						align: 'end',
					},
				},
				{
					element: '[data-tour="query-selector"]',
					popover: {
						title: 'Gespeicherte Suchanfragen',
						description:
							'Wechseln Sie zwischen Ihren gespeicherten Suchanfragen. Sie können Suchanfragen umbenennen, teilen, zusammenführen oder erweitern.',
						side: 'bottom',
						align: 'start',
					},
				},
				{
					element: '[data-tour="data-enrichment"]',
					popover: {
						title: 'Datenanreicherung',
						description:
							'Reichern Sie Ihre Leads mit zusätzlichen Daten an: Ansprechpartner, E-Mail-Adressen, Telefonnummern und Social-Media-Profile.',
						side: 'bottom',
						align: 'end',
					},
				},
				{
					element: '[data-tour="lead-table"]',
					popover: {
						title: 'Ergebnisse',
						description:
							'Hier sehen Sie alle gefundenen Unternehmen mit KI-Score, Kontaktanzahl, Finanzdaten und Standort. Klicken Sie auf eine Spaltenüberschrift zum Sortieren oder auf einen Firmennamen für Details.',
						side: 'top',
						align: 'center',
					},
				},
			],
		});
	}, [setTourCompleted]);

	useEffect(() => {
		if (tourCompleted || paused) return;

		const timeoutId = setTimeout(() => {
			driverRef.current = createDriver();
			driverRef.current.drive();
		}, 800);

		return () => {
			clearTimeout(timeoutId);
			driverRef.current?.destroy();
		};
	}, [tourCompleted, paused, createDriver]);

	const startTour = useCallback(() => {
		driverRef.current?.destroy();
		driverRef.current = createDriver();
		driverRef.current.drive();
	}, [createDriver]);

	return { startTour };
};
